import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
} from 'react'

import {
  HiCheckCircle,
  HiMiniExclamationTriangle,
  HiMiniPlus,
  HiOutlineArrowsRightLeft,
  HiOutlineShieldCheck,
} from 'react-icons/hi2'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/common/AlertDialog'
import Button from '@/components/common/Button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/common/Command'
import Popover from '@/components/common/PopoverV2'
import { Skeleton } from '@/components/common/Skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import AddOrganizationModal from '@/components/organizations/AddOrganizationModal'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { useIsAtLeastAdmin } from '@/hooks/billing/useBillingRole'
import { useEntitlementCheck } from '@/hooks/entitlements'
import {
  type UseCuiOrgSwitcherResult,
  useCuiOrgSwitcher,
} from '@/hooks/organization/useCuiOrgSwitcher'
import { useOrganization } from '@/hooks/organization/useOrganization'
import { useUpdateOrganization } from '@/hooks/organization/useUpdateOrganization'

import type { UUID } from '@/types/Id'
import type { Organization } from '@/types/Organization'

interface WhyDoWeAskThisProps {}

const STANDARD_DEPLOYMENT_DESCRIPTION = `Uses Sweetspot's commercial enclave, optimized for performance and general business applications.`
const CUI_DEPLOYMENT_DESCRIPTION = `Adheres to NIST 800-171 security controls and hosted on CMMC Level 2 certified and FedRAMP Moderate equivalent infrastructure, specifically engineered to manage Controlled Unclassified Information (CUI).`

export const WhyDoWeAskThis: React.FC<WhyDoWeAskThisProps> = memo(() => {
  return (
    <div className="flex flex-col gap-3 p-6 rounded-sm shadow-md">
      <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400">
        Important Security Information
      </h3>
      <div className="space-y-4">
        <p className="text-sm text-primary-700 dark:text-primary-300">
          Sweetspot offers two distinct organization security options to address
          varying security requirements:
        </p>

        <ul className="list-none space-y-3">
          <li className="flex items-start">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-1">
              <span className="text-white text-xs font-bold">1</span>
            </div>
            <div>
              <strong className="text-sm text-primary-800 dark:text-primary-200">
                Standard Deployment:
              </strong>
              <p className="text-sm text-primary-600 dark:text-primary-400">
                {STANDARD_DEPLOYMENT_DESCRIPTION}
              </p>
            </div>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mr-3 mt-1">
              <span className="text-white text-xs font-bold">2</span>
            </div>
            <div>
              <strong className="text-sm text-primary-800 dark:text-primary-200">
                CUI-Compliant Deployment:
              </strong>
              <p className="text-sm text-primary-600 dark:text-primary-400">
                {CUI_DEPLOYMENT_DESCRIPTION}
              </p>
            </div>
          </li>
        </ul>

        <p className="text-sm text-primary-700 dark:text-primary-300">
          Enabling CUI compliance for an organization ensures all workflows are
          automatically routed through our CMMC Level 2 certified
          infrastructure. While our standard deployment maintains rigorous
          security protocols, only the CUI-compliant deployment fulfills the
          stringent requirements for handling sensitive government information.
        </p>

        <p className="text-sm text-primary-700 dark:text-primary-300">
          This option allows organizations not handling CUI to leverage the
          enhanced performance of our standard deployment, while those managing
          CUI can ensure data processing aligns with federal security mandates.
        </p>

        <div className="bg-yellow-100 dark:bg-yellow-900 p-4 rounded-md">
          <p className="text-sm text-yellow-800 dark:text-yellow-200 font-semibold">
            Important: Once an organization is designated for CUI handling, this
            setting cannot be reversed due to security protocols.
          </p>
        </div>
      </div>
    </div>
  )
})

interface CuiTooltipProps extends React.ComponentProps<typeof TooltipTrigger> {
  children: React.ReactNode
}

export const CuiTooltip: React.FC<CuiTooltipProps> = memo(
  ({ children, ...props }) => {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger {...props}>{children}</TooltipTrigger>
        <TooltipContent className="max-w-[600px] p-0" inverseColors={true}>
          <WhyDoWeAskThis />
        </TooltipContent>
      </Tooltip>
    )
  },
)

interface OrgSwitcherListProps {
  organizations: Organization[]
  onSelect: (orgId: UUID) => void
  placeholder?: string
}

export const OrgSwitcherList: React.FC<OrgSwitcherListProps> = memo(
  ({ organizations, onSelect, placeholder = 'Search organizations...' }) => (
    <Command>
      <CommandInput placeholder={placeholder} containerClassName="px-1" />
      <CommandList className="max-h-[280px]">
        <CommandEmpty>No matching organizations.</CommandEmpty>
        <CommandGroup>
          {organizations.map((org) => (
            <CommandItem
              key={org.id}
              value={org.name}
              onSelect={() => onSelect(org.id)}
              className="cursor-pointer px-3 py-2"
            >
              <span className="truncate">{org.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  ),
)

interface CuiSafeOrganizationBlockProps {
  orgId: UUID
  featureName: string
  redirectTo: string | ((targetOrgId: UUID) => string)
  description?: string
}

export const CuiSafeOrganizationBlock: React.FC<CuiSafeOrganizationBlockProps> =
  memo(({ orgId, featureName, redirectTo, description }) => {
    const {
      isLoading,
      switchableOrganizations,
      hasSingleSwitchableOrganization,
      hasMultipleSwitchableOrganizations,
      singleSwitchableOrganization,
      popoverOpen,
      setPopoverOpen,
      handleSwitchOrganization,
      handleSwitchToSingleOrganization,
    } = useCuiOrgSwitcher({
      orgId,
      options: {
        redirectTo,
      },
    })

    const hasSwitchableOrgs =
      hasSingleSwitchableOrganization || hasMultipleSwitchableOrganizations
    const copy =
      description ??
      `${featureName} uses external data sources that are not permitted in CUI-safe organizations.`

    return (
      <div className="flex h-full w-full items-center justify-center bg-primary-50 dark:bg-primary-950">
        <div className="flex w-full max-w-md flex-col items-center justify-center px-4 py-20">
          <div
            className={twMerge(
              'mb-4 flex h-12 w-12 items-center justify-center rounded-xl',
              'bg-blue-50 ring-1 ring-blue-200',
              'dark:bg-blue-500/10 dark:ring-blue-500/20',
            )}
          >
            <HiOutlineShieldCheck className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>

          <h2 className="mb-1.5 text-[17px] font-semibold text-primary-900 dark:text-primary-100">
            CUI-Safe Organization
          </h2>
          <p className="max-w-sm text-center text-[13px] leading-relaxed text-primary-500 dark:text-primary-400">
            {copy}
            {(isLoading || hasSwitchableOrgs) &&
              ' Switch to a standard organization to use this feature.'}
          </p>

          {isLoading && (
            <Button
              type="button"
              variant="outline-compact"
              className="mt-6 gap-2"
              disabled={true}
              isLoading={true}
            >
              <HiOutlineArrowsRightLeft className="h-4 w-4" />
              Switch Organization
            </Button>
          )}

          {!isLoading &&
            hasSingleSwitchableOrganization &&
            singleSwitchableOrganization && (
              <Button
                type="button"
                variant="outline-compact"
                className="mt-6 gap-2"
                onClick={handleSwitchToSingleOrganization}
              >
                <HiOutlineArrowsRightLeft className="h-4 w-4" />
                Switch to {singleSwitchableOrganization.name}
              </Button>
            )}

          {!isLoading && hasMultipleSwitchableOrganizations && (
            <Popover
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
              modal={true}
            >
              <Popover.Trigger asChild>
                <Button
                  type="button"
                  variant="outline-compact"
                  className="mt-6 gap-2"
                >
                  <HiOutlineArrowsRightLeft className="h-4 w-4" />
                  Switch Organization
                </Button>
              </Popover.Trigger>
              <Popover.Content className="w-[320px] p-0">
                <OrgSwitcherList
                  organizations={switchableOrganizations}
                  onSelect={handleSwitchOrganization}
                />
              </Popover.Content>
            </Popover>
          )}
        </div>
      </div>
    )
  })

export const CuiCompliantCopy: React.FC = memo(() => {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-primary-700 dark:text-primary-300">
        This organization is configured to handle Controlled Unclassified
        Information (CUI) and uses our CMMC Level 2 certified and FedRAMP
        Moderate equivalent infrastructure. All workflows are automatically
        routed through secure channels that meet federal security mandates.
      </p>
    </div>
  )
})

export const NonCuiUpgradeCopy: React.FC = memo(() => {
  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-4">
        <p className="text-sm text-primary-700 dark:text-primary-300">
          This organization uses our standard deployment with rigorous security
          protocols and industry best practices, but is not configured to handle
          Controlled Unclassified Information (CUI).
        </p>
        <ul className="list-none space-y-3">
          <li className="flex items-start">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-3 mt-1">
              <span className="text-white text-xs font-bold">1</span>
            </div>
            <div>
              <strong className="text-sm text-primary-800 dark:text-primary-200">
                Current Configuration:
              </strong>
              <p className="text-sm text-primary-600 dark:text-primary-400">
                {STANDARD_DEPLOYMENT_DESCRIPTION}
              </p>
            </div>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mr-3 mt-1">
              <span className="text-white text-xs font-bold">2</span>
            </div>
            <div>
              <strong className="text-sm text-primary-800 dark:text-primary-200">
                Available Upgrade:
              </strong>
              <p className="text-sm text-primary-600 dark:text-primary-400">
                {CUI_DEPLOYMENT_DESCRIPTION}
              </p>
            </div>
          </li>
        </ul>
      </div>
    </div>
  )
})

interface NonCuiActionButtonsProps {
  organizationName: string
  onMigrate: () => void
  onCreateOrganization: () => void
}

export const NonCuiActionButtons: React.FC<NonCuiActionButtonsProps> = memo(
  ({ organizationName, onMigrate, onCreateOrganization }) => {
    const isAtLeastAdmin = useIsAtLeastAdmin()
    const { data: billingOrganization } = useBillingOrganization()

    const canCreateOrgs =
      billingOrganization !== undefined &&
      billingOrganization !== null &&
      billingOrganization.tier !== 'free' &&
      billingOrganization.tier !== 'basic' &&
      billingOrganization.num_organizations <
        billingOrganization.num_allowed_organizations

    const migrateTooltip = !isAtLeastAdmin
      ? 'You must be an administrator to migrate an organization to a CUI-compliant deployment.'
      : null

    const createTooltip = !isAtLeastAdmin
      ? 'You must be an administrator to create a new organization.'
      : !canCreateOrgs
        ? "You've reached the maximum number of allowed organizations. Please reach out to support to increase your limit."
        : null

    return (
      <div className="flex flex-col gap-2">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild={true}>
            <Button
              type="button"
              variant="danger-compact"
              onClick={onMigrate}
              disabled={!isAtLeastAdmin}
              className="w-full justify-center text-center"
            >
              Migrate {organizationName} to CUI-Compliant Deployment
            </Button>
          </TooltipTrigger>
          {migrateTooltip && (
            <TooltipContent className="max-w-[300px]">
              {migrateTooltip}
            </TooltipContent>
          )}
        </Tooltip>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild={true}>
            <Button
              type="button"
              variant="outline-compact"
              onClick={onCreateOrganization}
              disabled={!isAtLeastAdmin || !canCreateOrgs}
              className="w-full justify-center gap-2 text-center"
            >
              <HiMiniPlus size={16} className="flex-shrink-0" />
              <span>Create New CUI Organization</span>
            </Button>
          </TooltipTrigger>
          {createTooltip && (
            <TooltipContent className="max-w-[300px]">
              {createTooltip}
            </TooltipContent>
          )}
        </Tooltip>
      </div>
    )
  },
)

NonCuiActionButtons.displayName = 'Cui.NonCuiActionButtons'

const CuiSwitcherContext = createContext<UseCuiOrgSwitcherResult | null>(null)

export function useCuiSwitcherContext(): UseCuiOrgSwitcherResult {
  const ctx = useContext(CuiSwitcherContext)
  if (!ctx) {
    throw new Error(
      'useCuiSwitcherContext must be used within a CuiOrgSwitchPopover',
    )
  }
  return ctx
}

interface CuiOrgSwitchPopoverProps {
  orgId: UUID
  children: React.ReactNode
}

type PendingDialogAction = 'migrate' | 'create' | null

const CuiOrgSwitchPopover: React.FC<CuiOrgSwitchPopoverProps> = memo(
  ({ orgId, children }) => {
    const { data: hasCuiEntitlement, isLoading: isEntitlementLoading } =
      useEntitlementCheck({ entitlement: 'cui_compliance' })
    const { data: organization } = useOrganization(orgId)

    const switcher = useCuiOrgSwitcher({ orgId })
    const {
      isLoading: isSwitcherLoading,
      isCompliant,
      switchableOrganizations,
      hasSingleSwitchableOrganization,
      hasMultipleSwitchableOrganizations,
      popoverOpen,
      setPopoverOpen,
      handleSwitchOrganization,
    } = switcher
    const [migrateDialogOpen, setMigrateDialogOpen] = React.useState(false)
    const [addOrganizationModalOpen, setAddOrganizationModalOpen] =
      React.useState(false)
    const [pendingDialogAction, setPendingDialogAction] =
      React.useState<PendingDialogAction>(null)

    const handleOpenChange = useCallback(
      (open: boolean) => {
        setPopoverOpen(open)
      },
      [setPopoverOpen],
    )

    const handleOpenMigrateDialog = useCallback(() => {
      setPendingDialogAction('migrate')
      setPopoverOpen(false)
    }, [setPopoverOpen])

    const handleOpenAddOrganizationModal = useCallback(() => {
      setPendingDialogAction('create')
      setPopoverOpen(false)
    }, [setPopoverOpen])

    useEffect(() => {
      if (popoverOpen || pendingDialogAction === null) {
        return
      }

      if (pendingDialogAction === 'migrate') {
        setMigrateDialogOpen(true)
      } else {
        setAddOrganizationModalOpen(true)
      }

      setPendingDialogAction(null)
    }, [pendingDialogAction, popoverOpen])

    if (isSwitcherLoading || isEntitlementLoading) {
      if (hasCuiEntitlement === false) {
        return null
      }
      return (
        <div className="flex flex-col items-center gap-2 mb-3.5 mt-1">
          <div className="w-10 h-10">
            <Skeleton className="w-full h-full" />
          </div>
          <Skeleton className="w-10 h-2" />
        </div>
      )
    }

    if (hasSingleSwitchableOrganization) {
      return (
        <CuiSwitcherContext.Provider value={switcher}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild={true}>{children}</TooltipTrigger>
            <TooltipContent
              className="max-w-[260px]"
              side="right"
              align="start"
            >
              {isCompliant
                ? 'Click to switch to your standard organization.'
                : 'Click to switch to your CUI-safe organization.'}
            </TooltipContent>
          </Tooltip>
        </CuiSwitcherContext.Provider>
      )
    }

    if (hasMultipleSwitchableOrganizations) {
      return (
        <CuiSwitcherContext.Provider value={switcher}>
          <Popover
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            modal={true}
          >
            <Tooltip delayDuration={0}>
              <Popover.Trigger asChild={true}>
                <TooltipTrigger asChild={true}>{children}</TooltipTrigger>
              </Popover.Trigger>
              <TooltipContent
                className="max-w-[260px]"
                side="right"
                align="start"
              >
                {isCompliant
                  ? 'Click to switch to a Non-CUI organization.'
                  : 'Click to switch to a CUI-safe organization.'}
              </TooltipContent>
            </Tooltip>
            <Popover.Content className="w-[320px] p-0">
              <OrgSwitcherList
                organizations={switchableOrganizations}
                onSelect={handleSwitchOrganization}
                placeholder={
                  isCompliant
                    ? 'Search Non-CUI organizations...'
                    : 'Search CUI-safe organizations...'
                }
              />
            </Popover.Content>
          </Popover>
        </CuiSwitcherContext.Provider>
      )
    }

    return (
      <CuiSwitcherContext.Provider value={switcher}>
        <MigrateOrgToCui
          orgId={orgId}
          open={migrateDialogOpen}
          setOpen={setMigrateDialogOpen}
        />
        <AddOrganizationModal
          open={addOrganizationModalOpen}
          setOpen={setAddOrganizationModalOpen}
          initialCuiComplianceEnabled={true}
        />
        <Popover
          open={popoverOpen}
          onOpenChange={handleOpenChange}
          modal={true}
        >
          <Popover.Trigger asChild={true}>{children}</Popover.Trigger>
          <Popover.Content className="w-[424px] p-4">
            {isCompliant && !hasMultipleSwitchableOrganizations ? (
              <CuiCompliantCopy />
            ) : (
              <div className="flex flex-col gap-4">
                <NonCuiUpgradeCopy />
                {organization && (
                  <NonCuiActionButtons
                    organizationName={organization.name}
                    onMigrate={handleOpenMigrateDialog}
                    onCreateOrganization={handleOpenAddOrganizationModal}
                  />
                )}
              </div>
            )}
          </Popover.Content>
        </Popover>
      </CuiSwitcherContext.Provider>
    )
  },
)

interface CuiSidebarStatusProps {
  orgId: UUID
}

const SidebarButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithRef<typeof Button>
>(({ onClick, ...rest }, ref) => {
  const {
    isCompliant,
    hasSingleSwitchableOrganization,
    handleSwitchToSingleOrganization,
  } = useCuiSwitcherContext()

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (hasSingleSwitchableOrganization) {
        handleSwitchToSingleOrganization()
        return
      }
      onClick?.(e)
    },
    [
      hasSingleSwitchableOrganization,
      handleSwitchToSingleOrganization,
      onClick,
    ],
  )

  return (
    <Button
      ref={ref}
      type="button"
      {...rest}
      onClick={handleClick}
      variant="ghost-compact"
      // this button lives on the sidebar rail, which the professional theme
      // paints dark; the 700-weight label it uses on a light rail goes muddy
      // there, so it takes the same lighter shade dark mode does
      className={
        isCompliant
          ? 'h-auto min-h-[60px] w-full flex-col gap-2 whitespace-normal rounded-lg px-1 py-1 text-green-700 hover:bg-green-50 dark:text-green-300 dark:hover:bg-green-950/30 professional:text-green-300 professional:hover:bg-white/10'
          : 'h-auto min-h-[60px] w-full flex-col gap-2 whitespace-normal rounded-lg px-1 py-1 text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40 professional:text-red-300 professional:hover:bg-white/10'
      }
    >
      <span
        className={twMerge(
          'flex h-9 w-9 items-center justify-center rounded-lg shadow-sm ring-1',
          isCompliant
            ? 'bg-green-100 text-green-700 shadow-green-900/10 ring-green-200 dark:bg-green-900/40 dark:text-green-200 dark:ring-green-800'
            : 'bg-red-600 text-white shadow-red-900/20 ring-red-700/70 dark:bg-red-600 dark:ring-red-400/60',
        )}
      >
        {isCompliant ? (
          <HiCheckCircle
            size={22}
            className="flex-shrink-0 text-green-600 dark:text-green-400"
          />
        ) : (
          <HiMiniExclamationTriangle size={24} className="flex-shrink-0" />
        )}
      </span>
      <span className="whitespace-nowrap text-[11px] font-semibold leading-[1.05] tracking-[0.01em]">
        {isCompliant ? 'CUI Safe' : 'Non-CUI'}
      </span>
    </Button>
  )
})

export const CuiSidebarStatus: React.FC<CuiSidebarStatusProps> = memo(
  ({ orgId }) => (
    <CuiOrgSwitchPopover orgId={orgId}>
      <SidebarButton />
    </CuiOrgSwitchPopover>
  ),
)

const MobileSidebarButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithRef<typeof Button>
>(({ onClick, ...rest }, ref) => {
  const {
    isCompliant,
    hasSingleSwitchableOrganization,
    handleSwitchToSingleOrganization,
  } = useCuiSwitcherContext()

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (hasSingleSwitchableOrganization) {
        handleSwitchToSingleOrganization()
        return
      }
      onClick?.(e)
    },
    [
      hasSingleSwitchableOrganization,
      handleSwitchToSingleOrganization,
      onClick,
    ],
  )

  return (
    <Button
      ref={ref}
      type="button"
      {...rest}
      onClick={handleClick}
      variant={isCompliant ? 'ghost-compact' : 'danger-compact'}
      className={
        isCompliant
          ? 'h-auto min-h-[44px] w-full justify-start gap-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-left text-green-800 hover:bg-green-100 dark:border-green-900/70 dark:bg-green-950/30 dark:text-green-200 dark:hover:bg-green-950/50'
          : 'h-auto min-h-[44px] w-full justify-start gap-2 rounded-xl px-3 py-2 text-left text-white hover:bg-red-500 dark:hover:bg-red-500'
      }
    >
      {isCompliant ? (
        <HiCheckCircle
          size={18}
          className="flex-shrink-0 text-green-600 dark:text-green-400"
        />
      ) : (
        <HiMiniExclamationTriangle size={18} className="flex-shrink-0" />
      )}
      <span className="text-sm font-semibold">
        {isCompliant ? 'CUI Safe' : 'Non-CUI'}
      </span>
    </Button>
  )
})

export const CuiMobileSidebarStatus: React.FC<CuiSidebarStatusProps> = memo(
  ({ orgId }) => (
    <CuiOrgSwitchPopover orgId={orgId}>
      <MobileSidebarButton />
    </CuiOrgSwitchPopover>
  ),
)

interface MigrateOrgToCuiProps {
  orgId: UUID
  open: boolean
  setOpen: (open: boolean) => void
}

export const MigrateOrgToCui: React.FC<MigrateOrgToCuiProps> = memo(
  ({ orgId, open, setOpen }) => {
    const isAtLeastAdmin = useIsAtLeastAdmin()

    const { mutateAsync: updateOrganization, isPending } =
      useUpdateOrganization()
    const { data: organization } = useOrganization(orgId)

    const handleCancel = useCallback(() => {
      setOpen(false)
    }, [setOpen])

    const handleMigrate = useCallback(async () => {
      if (!organization || !isAtLeastAdmin) {
        return
      }
      await updateOrganization(
        [
          orgId,
          {
            ...organization,
            cui_compliance_enabled: true,
          },
        ],
        {
          onSuccess: () => {
            toast.success('Successfully migrated to CUI-compliant deployment!')
            setOpen(false)
          },
        },
      )
    }, [isAtLeastAdmin, organization, orgId, updateOrganization, setOpen])

    return (
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Migrate Organization to CUI-Compliant Sweetspot Deployment
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <div className="space-y-3">
              <p>
                You are about to migrate this organization to a compliant
                deployment that adheres to NIST 800-171 security controls.
              </p>
              <p className="font-semibold text-red-600 dark:text-red-400">
                Important: This action cannot be reversed to ensure Controlled
                Unclassified Information (CUI) data stays in our CMMC Level 2
                certified environment.
              </p>
              <p>
                After migration, all workflows will automatically route through
                our CMMC Level 2 compliant infrastructure to ensure proper
                handling of CUI.
              </p>
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <Button
              type="button"
              variant="outline-compact"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild={true}>
                <Button
                  type="button"
                  variant="danger-compact"
                  onClick={handleMigrate}
                  isLoading={isPending}
                  disabled={isPending || !isAtLeastAdmin}
                >
                  Migrate
                </Button>
              </TooltipTrigger>
              {!isAtLeastAdmin && (
                <TooltipContent className="max-w-[300px]">
                  You must be an administrator to migrate an organization to a
                  CUI-compliant deployment.
                </TooltipContent>
              )}
            </Tooltip>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  },
)

WhyDoWeAskThis.displayName = 'Cui.WhyDoWeAskThis'
CuiTooltip.displayName = 'Cui.CuiTooltip'
OrgSwitcherList.displayName = 'Cui.OrgSwitcherList'
CuiCompliantCopy.displayName = 'Cui.CuiCompliantCopy'
NonCuiUpgradeCopy.displayName = 'Cui.NonCuiUpgradeCopy'
CuiOrgSwitchPopover.displayName = 'Cui.CuiOrgSwitchPopover'
SidebarButton.displayName = 'Cui.SidebarButton'
MobileSidebarButton.displayName = 'Cui.MobileSidebarButton'
CuiSidebarStatus.displayName = 'Cui.CuiSidebarStatus'
CuiMobileSidebarStatus.displayName = 'Cui.CuiMobileSidebarStatus'
MigrateOrgToCui.displayName = 'Cui.MigrateOrgToCui'
